import React, { useEffect, useState } from "react";
import { supabase } from "../../supabaseClient.js";
import "../../styles/ReceiveWax.css";

export default function WaxStoneLedger() {
  const [workers, setWorkers] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState("ALL");

  const [ledgerData, setLedgerData] = useState([]);
  const [popupLot, setPopupLot] = useState(null);

  // ============================================================
  // LOAD WORKERS
  // ============================================================
  useEffect(() => {
    loadWorkers();
  }, []);

  const loadWorkers = async () => {
    const { data, error } = await supabase
      .from("WORKERS")
      .select("id, worker_name")
      .order("worker_name", { ascending: true });

    if (error) {
      console.error("WORKERS ERROR:", error);
      return;
    }

    setWorkers(data || []);
  };

  // ============================================================
  // LOAD LEDGER DATA (PER WORKER)
  // ============================================================
  useEffect(() => {
    fetchLedgerData();
  }, [selectedWorker]);

  const fetchLedgerData = async () => {
    try {
      // 1️⃣ Fetch completed lots (optionally filtered by worker)
      let lotQuery = supabase
        .from("WAX_LOTS")
        .select("*")
        .in("status", ["COMPLETED", "COMPLETED_WAX"])
        .order("issue_date", { ascending: false });

      if (selectedWorker !== "ALL") {
        lotQuery = lotQuery.eq("worker_id", selectedWorker);
      }

      const { data: lots, error: lotErr } = await lotQuery;
      if (lotErr) {
        console.error("LOTS ERROR:", lotErr);
        setLedgerData([]);
        return;
      }

      if (!lots || lots.length === 0) {
        setLedgerData([]);
        return;
      }

      const lotIds = lots.map((l) => l.id);

      // 2️⃣ Fetch wax items in those lots
      const { data: waxItems, error: waxErr } = await supabase
        .from("WAX_ITEMS")
        .select("*, ITEMS:item_id(item_name)")
        .in("lot_id", lotIds);

      if (waxErr) {
        console.error("WAX_ITEMS ERROR:", waxErr);
        setLedgerData([]);
        return;
      }

      if (!waxItems || waxItems.length === 0) {
        setLedgerData([]);
        return;
      }

      const itemIds = waxItems.map((w) => w.id);

      // 3️⃣ Fetch stone usage (WAX_RECEIVE_ITEMS) for those items
      const { data: stones, error: stoneErr } = await supabase
        .from("WAX_RECEIVE_ITEMS")
        .select("*")
        .in("item_id", itemIds);

      if (stoneErr) {
        console.error("WAX_RECEIVE_ITEMS ERROR:", stoneErr);
        // still continue but with 0 stones
      }

      const stoneRows = stones || [];

      // 4️⃣ Merge stones into wax items using item_id (= WAX_ITEMS.id)
      const mergedItems = waxItems.map((item) => {
        const related = stoneRows.filter((s) => s.item_id === item.id);
        const micro =
          related.reduce((sum, r) => sum + Number(r.micro_stone || 0), 0) || 0;
        const big =
          related.reduce((sum, r) => sum + Number(r.big_stone || 0), 0) || 0;

        return {
          ...item,
          micro_stone: micro,
          big_stone: big,
        };
      });

      // 5️⃣ Build final ledger rows { lot, item }
      const finalRows = mergedItems.map((item) => {
        const lot = lots.find((l) => l.id === item.lot_id);
        return { lot, item };
      });

      setLedgerData(finalRows);
    } catch (err) {
      console.error("FINAL LEDGER ERROR:", err);
      setLedgerData([]);
    }
  };

  // ============================================================
  // POPUP: LOAD FULL LOT DETAILS (PER LOT)
  // ============================================================
  const openLotPopup = async (lot) => {
    try {
      // Get all wax items for this lot
      const { data: waxItems, error: waxErr } = await supabase
        .from("WAX_ITEMS")
        .select("*, ITEMS:item_id(item_name)")
        .eq("lot_id", lot.id);

      if (waxErr) {
        console.error("POPUP WAX_ITEMS ERROR:", waxErr);
        return;
      }

      if (!waxItems || waxItems.length === 0) {
        setPopupLot({ ...lot, items: [] });
        return;
      }

      const itemIds = waxItems.map((w) => w.id);

      // Get all stone records for those items in this lot
      const { data: stones, error: stoneErr } = await supabase
        .from("WAX_RECEIVE_ITEMS")
        .select("*")
        .eq("lot_id", lot.id)
        .in("item_id", itemIds);

      if (stoneErr) {
        console.error("POPUP STONES ERROR:", stoneErr);
      }

      const stoneRows = stones || [];

      const finalItems = waxItems.map((item) => {
        const related = stoneRows.filter((s) => s.item_id === item.id);
        const micro =
          related.reduce((sum, r) => sum + Number(r.micro_stone || 0), 0) || 0;
        const big =
          related.reduce((sum, r) => sum + Number(r.big_stone || 0), 0) || 0;

        return {
          ...item,
          micro_stone: micro,
          big_stone: big,
        };
      });

      setPopupLot({ ...lot, items: finalItems });
    } catch (err) {
      console.error("POPUP ERROR:", err);
    }
  };

  // ============================================================
  // POPUP COMPONENT
  // ============================================================
  const LotPopup = ({ lot, onClose }) => (
    <div className="popup-overlay">
      <div className="popup-box" style={{ maxWidth: "900px" }}>
        <h2>Lot Details — {lot.lot_no}</h2>
        <p>Date: {lot.issue_date?.slice(0, 10)}</p>

        <table className="wax-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Design</th>
              <th>Gross</th>
              <th>Net</th>
              <th>Pcs</th>
              <th>Micro</th>
              <th>Big</th>
            </tr>
          </thead>
          <tbody>
            {lot.items?.map((i) => (
              <tr key={i.id}>
                <td>{i.ITEMS?.item_name}</td>
                <td>{i.design_code}</td>
                <td>{i.gross_weight}</td>
                <td>{i.net_weight}</td>
                <td>{i.pieces_count}</td>
                <td>{i.micro_stone}</td>
                <td>{i.big_stone}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <button className="wax-btn" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );

  // ============================================================
  // MAIN UI
  // ============================================================
  return (
    <div className="receive-container">
      <h2 className="page-title">Wax Stone Ledger</h2>

      {/* Worker Filter */}
      <div style={{ marginBottom: "20px" }}>
        <label style={{ fontWeight: "bold" }}>Filter by Worker:</label>
        <select
          value={selectedWorker}
          onChange={(e) => setSelectedWorker(e.target.value)}
          style={{
            padding: "8px",
            marginLeft: "10px",
            borderRadius: "6px",
            border: "1px solid #ccc",
          }}
        >
          <option value="ALL">All Workers</option>
          {workers.map((w) => (
            <option key={w.id} value={w.id}>
              {w.worker_name}
            </option>
          ))}
        </select>
      </div>

      {/* Ledger Table */}
      <table className="wax-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Lot No</th>
            <th>Item</th>
            <th>Design</th>
            <th>Micro</th>
            <th>Big</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {ledgerData.length === 0 && (
            <tr>
              <td colSpan="7" style={{ textAlign: "center" }}>
                No records found.
              </td>
            </tr>
          )}

          {ledgerData.map((row, idx) => (
            <tr key={idx}>
              <td>{row.lot.issue_date?.slice(0, 10)}</td>
              <td>{row.lot.lot_no}</td>
              <td>{row.item.ITEMS?.item_name}</td>
              <td>{row.item.design_code}</td>
              <td>{row.item.micro_stone}</td>
              <td>{row.item.big_stone}</td>
              <td>
                <button
                  className="wax-btn"
                  onClick={() => openLotPopup(row.lot)}
                >
                  View
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {popupLot && <LotPopup lot={popupLot} onClose={() => setPopupLot(null)} />}
    </div>
  );
}
